export const MEDICINE_CODE_PAD_LENGTH = 4;

export type MedicineCodeScale = 'mother' | 'decimal' | 'centesimal' | 'lm';

export interface MedicineCodeMedicineRef {
  id?: string | number;
  name?: string;
  code?: string;
  potency?: string;
}

export interface MedicineCodeSuggestion {
  scale: MedicineCodeScale | null;
  prefix: string;
  suggested_code: string;
  last_code: string | null;
  last_number: number;
  next_number: number;
  pad_length: number;
  needs_scale?: boolean;
}

export function getMedicineCodePrefix(name: string): string {
  const trimmed = String(name || '').trim();
  if (!trimmed) {
    return 'A';
  }

  const firstWord = trimmed.split(/\s+/)[0] || '';
  const match = firstWord.match(/[A-Za-z]/);
  return match ? match[0].toUpperCase() : 'A';
}

export function resolveMedicineCodeScale(
  potency?: string | null,
  scale?: string | null,
): MedicineCodeScale | null {
  const normalizedScale = String(scale || '').trim();
  if (normalizedScale === 'mother' || normalizedScale === 'decimal' || normalizedScale === 'centesimal' || normalizedScale === 'lm') {
    return normalizedScale;
  }

  const normalizedPotency = String(potency || '').trim();
  if (!normalizedPotency) {
    return null;
  }

  if (normalizedPotency === 'Ø' || normalizedPotency === 'Q' || normalizedPotency.toUpperCase() === '1X') {
    return 'mother';
  }

  if (normalizedPotency.endsWith('X')) {
    return 'decimal';
  }

  if (normalizedPotency.toUpperCase().startsWith('LM')) {
    return 'lm';
  }

  // Hahnemannian LM notation: 0/1 … 0/30 (same scale as LM1 … LM30)
  if (/^0\/\d+$/.test(normalizedPotency)) {
    return 'lm';
  }

  return 'centesimal';
}

function normalizeMedicineName(name: string): string {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Alphanumeric key so minor spacing/punctuation still match.
 * Also strips trailing Q/Ø often baked into legacy mother-tincture names.
 */
function normalizeMedicineNameKey(name: string): string {
  let lower = normalizeMedicineName(name);
  lower = lower.replace(/[\s\-_]*(q|ø|⌀)$/u, '');
  return lower.replace(/[^a-z0-9]+/g, '');
}

function normalizeCodeString(code: string): string {
  return String(code || '').trim().toUpperCase().replace(/\s+/g, '');
}

function suggestCentesimalCode(letter: string, codes: string[]): MedicineCodeSuggestion {
  const normalizedLetter = letter.toUpperCase();
  let lastCode: string | null = null;
  let lastNumber = 0;

  for (const rawCode of codes) {
    const code = normalizeCodeString(rawCode);
    const match = code.match(new RegExp(`^${normalizedLetter}(\\d{4})$`));
    if (!match) {
      continue;
    }

    const number = Number.parseInt(match[1], 10);
    if (number > lastNumber) {
      lastNumber = number;
      lastCode = code;
    }
  }

  const nextNumber = lastNumber + 1;

  return {
    scale: 'centesimal',
    prefix: normalizedLetter,
    suggested_code: `${normalizedLetter}${String(nextNumber).padStart(MEDICINE_CODE_PAD_LENGTH, '0')}`,
    last_code: lastCode,
    last_number: lastNumber,
    next_number: nextNumber,
    pad_length: MEDICINE_CODE_PAD_LENGTH,
    needs_scale: false,
  };
}

/** Parses BC-A1 and spaced forms like "BC- C15". */
function parsePrefixedLetterCode(code: string, familyPrefix: 'BC'): { letter: string; number: number } | null {
  const normalized = normalizeCodeString(code);
  const match = normalized.match(new RegExp(`^${familyPrefix}-([A-Z])(\\d+)(?:-\\d+)?$`));
  if (!match) {
    return null;
  }

  return {
    letter: match[1],
    number: Number.parseInt(match[2], 10),
  };
}

/** BC: simple sequential codes — BC-A1, BC-A2. */
function suggestPrefixedLetterCode(
  familyPrefix: 'BC',
  scale: 'decimal',
  letter: string,
  medicines: MedicineCodeMedicineRef[],
): MedicineCodeSuggestion {
  const normalizedLetter = letter.toUpperCase();
  let lastCode: string | null = null;
  let lastNumber = 0;

  for (const medicine of medicines) {
    const parsed = parsePrefixedLetterCode(String(medicine?.code || ''), familyPrefix);
    if (!parsed || parsed.letter !== normalizedLetter) {
      continue;
    }

    if (parsed.number > lastNumber) {
      lastNumber = parsed.number;
      lastCode = `${familyPrefix}-${normalizedLetter}${parsed.number}`;
    }
  }

  const nextNumber = lastNumber + 1;
  const prefix = `${familyPrefix}-${normalizedLetter}`;

  return {
    scale,
    prefix,
    suggested_code: `${prefix}${nextNumber}`,
    last_code: lastCode,
    last_number: lastNumber,
    next_number: nextNumber,
    pad_length: 0,
    needs_scale: false,
  };
}

function isMotherOneXPotency(potency?: string | null): boolean {
  return String(potency || '').trim().toUpperCase() === '1X';
}

function formatMotherTinctureCode(letter: string, number: number, isX: boolean): string {
  return `MT-${letter.toUpperCase()}${number}${isX ? 'X' : ''}`;
}

function parseMotherTinctureCode(code: string): { letter: string; number: number; isX: boolean } | null {
  const normalized = normalizeCodeString(code);
  const match = normalized.match(/^MT-([A-Z])(\d+)(X)?$/);
  if (!match) {
    return null;
  }

  return {
    letter: match[1],
    number: Number.parseInt(match[2], 10),
    isX: match[3] === 'X',
  };
}

/**
 * Mother tincture: Q/Ø → MT-A1, 1X → MT-A1X (same number for the same medicine).
 */
function suggestMotherTinctureCode(
  name: string,
  letter: string,
  potency: string | null | undefined,
  medicines: MedicineCodeMedicineRef[],
): MedicineCodeSuggestion {
  const normalizedLetter = letter.toUpperCase();
  const normalizedName = normalizeMedicineNameKey(name);
  const wantsX = isMotherOneXPotency(potency);
  const prefix = `MT-${normalizedLetter}`;

  if (!normalizedName) {
    return {
      scale: 'mother',
      prefix,
      suggested_code: '',
      last_code: null,
      last_number: 0,
      next_number: 0,
      pad_length: 0,
      needs_scale: false,
    };
  }

  let existingNumber: number | null = null;
  for (const medicine of medicines) {
    if (normalizeMedicineNameKey(medicine?.name || '') !== normalizedName) {
      continue;
    }

    const parsed = parseMotherTinctureCode(String(medicine?.code || ''));
    if (!parsed || parsed.letter !== normalizedLetter) {
      continue;
    }

    if (existingNumber === null || parsed.number < existingNumber) {
      existingNumber = parsed.number;
    }
  }

  if (existingNumber !== null) {
    let lastCode: string | null = null;
    for (const medicine of medicines) {
      if (normalizeMedicineNameKey(medicine?.name || '') !== normalizedName) {
        continue;
      }
      const parsed = parseMotherTinctureCode(String(medicine?.code || ''));
      if (parsed && parsed.letter === normalizedLetter && parsed.number === existingNumber) {
        lastCode = formatMotherTinctureCode(normalizedLetter, parsed.number, parsed.isX);
        break;
      }
    }

    return {
      scale: 'mother',
      prefix,
      suggested_code: formatMotherTinctureCode(normalizedLetter, existingNumber, wantsX),
      last_code: lastCode,
      last_number: existingNumber,
      next_number: existingNumber,
      pad_length: 0,
      needs_scale: false,
    };
  }

  let lastCode: string | null = null;
  let lastNumber = 0;
  for (const medicine of medicines) {
    const parsed = parseMotherTinctureCode(String(medicine?.code || ''));
    if (!parsed || parsed.letter !== normalizedLetter) {
      continue;
    }

    if (parsed.number > lastNumber) {
      lastNumber = parsed.number;
      lastCode = formatMotherTinctureCode(normalizedLetter, parsed.number, parsed.isX);
    }
  }

  const nextNumber = lastNumber + 1;

  return {
    scale: 'mother',
    prefix,
    suggested_code: formatMotherTinctureCode(normalizedLetter, nextNumber, wantsX),
    last_code: lastCode,
    last_number: lastNumber,
    next_number: nextNumber,
    pad_length: 0,
    needs_scale: false,
  };
}

interface ParsedLmCode {
  code: string;
  letter: string;
  base: string;
  baseNum: number;
  suffix: number;
}

function parseLmCode(code: string): ParsedLmCode | null {
  const normalized = normalizeCodeString(code);
  const match = normalized.match(/^LM([A-Z])(\d{2})-(\d+)$/);
  if (!match) {
    return null;
  }

  return {
    code: normalized,
    letter: match[1],
    base: `LM${match[1]}${match[2]}`,
    baseNum: Number.parseInt(match[2], 10),
    suffix: Number.parseInt(match[3], 10),
  };
}

function pickPreferredLmBase(suffixByBase: Record<string, number>, letter: string): string {
  const normalizedLetter = letter.toUpperCase();
  const bases = Object.keys(suffixByBase);
  const matchingLetter = bases.filter((base) => base.startsWith(`LM${normalizedLetter}`));
  const candidates = matchingLetter.length > 0 ? matchingLetter : bases;

  candidates.sort((a, b) => {
    const numA = Number.parseInt(a.slice(3), 10);
    const numB = Number.parseInt(b.slice(3), 10);
    return numA - numB;
  });

  return candidates[0];
}

function suggestLmCode(
  name: string,
  letter: string,
  medicines: MedicineCodeMedicineRef[],
): MedicineCodeSuggestion {
  const normalizedLetter = letter.toUpperCase();
  const normalizedName = normalizeMedicineNameKey(name);

  if (!normalizedName) {
    return {
      scale: 'lm',
      prefix: `LM${normalizedLetter}`,
      suggested_code: '',
      last_code: null,
      last_number: 0,
      next_number: 0,
      pad_length: 2,
      needs_scale: false,
    };
  }

  const suffixByBase: Record<string, number> = {};

  for (const medicine of medicines) {
    if (normalizeMedicineNameKey(medicine?.name || '') !== normalizedName) {
      continue;
    }

    const parsed = parseLmCode(String(medicine?.code || ''));
    if (!parsed) {
      continue;
    }

    const currentMax = suffixByBase[parsed.base];
    if (currentMax === undefined || parsed.suffix > currentMax) {
      suffixByBase[parsed.base] = parsed.suffix;
    }
  }

  const bases = Object.keys(suffixByBase);
  if (bases.length > 0) {
    const preferredBase = pickPreferredLmBase(suffixByBase, normalizedLetter);
    const maxSuffix = suffixByBase[preferredBase];
    const nextSuffix = maxSuffix + 1;

    return {
      scale: 'lm',
      prefix: preferredBase,
      suggested_code: `${preferredBase}-${nextSuffix}`,
      last_code: maxSuffix > 0 ? `${preferredBase}-${maxSuffix}` : null,
      last_number: maxSuffix,
      next_number: nextSuffix,
      pad_length: 2,
      needs_scale: false,
    };
  }

  let lastCode: string | null = null;
  let lastNumber = 0;

  for (const medicine of medicines) {
    const parsed = parseLmCode(String(medicine?.code || ''));
    if (!parsed || parsed.letter !== normalizedLetter) {
      continue;
    }

    if (parsed.baseNum > lastNumber) {
      lastNumber = parsed.baseNum;
      lastCode = parsed.code;
    }
  }

  const nextNumber = lastNumber + 1;
  const base = `LM${normalizedLetter}${String(nextNumber).padStart(2, '0')}`;

  return {
    scale: 'lm',
    prefix: base,
    suggested_code: `${base}-1`,
    last_code: lastCode,
    last_number: lastNumber,
    next_number: 1,
    pad_length: 2,
    needs_scale: false,
  };
}

export function suggestNextMedicineCode(
  name: string,
  existingMedicines: MedicineCodeMedicineRef[],
  scale?: string | null,
  potency?: string | null,
): MedicineCodeSuggestion {
  const letter = getMedicineCodePrefix(name);
  const resolvedScale = resolveMedicineCodeScale(potency, scale);

  if (!resolvedScale) {
    return {
      scale: null,
      prefix: letter,
      suggested_code: '',
      last_code: null,
      last_number: 0,
      next_number: 0,
      pad_length: MEDICINE_CODE_PAD_LENGTH,
      needs_scale: true,
    };
  }

  const codes = existingMedicines
    .map((medicine) => normalizeCodeString(medicine?.code || ''))
    .filter(Boolean);

  switch (resolvedScale) {
    case 'mother':
      return suggestMotherTinctureCode(name, letter, potency, existingMedicines);
    case 'decimal':
      return suggestPrefixedLetterCode('BC', 'decimal', letter, existingMedicines);
    case 'lm':
      return suggestLmCode(name, letter, existingMedicines);
    default:
      return suggestCentesimalCode(letter, codes);
  }
}

export function normalizeMedicineCode(code: string): string {
  return normalizeCodeString(code);
}

export function isMedicineCodeInUse(
  code: string,
  existingMedicines: Array<{ id?: string | number; code?: string }>,
  excludeId?: string | number,
): boolean {
  const normalized = normalizeMedicineCode(code);
  if (!normalized) {
    return false;
  }

  const exclude = excludeId !== undefined && excludeId !== null ? String(excludeId) : '';

  return existingMedicines.some((medicine) => {
    if (exclude && String(medicine?.id ?? '') === exclude) {
      return false;
    }

    return normalizeMedicineCode(medicine?.code || '') === normalized;
  });
}

export function getMedicineCodeScaleLabel(scale: MedicineCodeScale | null | undefined): string {
  switch (scale) {
    case 'mother':
      return 'Mother tincture (Ø / Q)';
    case 'decimal':
      return 'Decimal (X)';
    case 'lm':
      return 'LM';
    default:
      return 'Centesimal (C)';
  }
}
